'use client';

import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Breadcrumb, type BreadcrumbItem } from '@/components/molecules/Breadcrumb';
import TrailingLink from '@/components/atoms/TrailingLink';
import styles from './PageHeroSection.module.scss';

export interface PageHeroSectionProps {
  title: string;
  description: string;
  subtitle?: string;
  heroIcon?: string;
  breadcrumbs: BreadcrumbItem[];
  metadata?: {
    publishedAt?: string;
    updatedAt?: string;
    author?: string;
    readingTime?: string;
    category?: string;
    tags?: string[];
  };
  level: 1 | 2 | 3;
  // Level 1 specific - Call to Action
  ctaButtons?: Array<{
    text: string;
    href: string;
    variant?: 'primary' | 'secondary';
  }>;
  // Level 2 specific - Hub stats
  hubStats?: Array<{
    number: string | number;
    label: string;
    icon?: string;
  }>;
  // Level 3 specific - Enhanced metadata display
  showContentInfo?: boolean;
  children?: ReactNode;
}

// Unified animation variants for all levels
const containerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut' as const,
      staggerChildren: 0.1,
    }
  }
};

const itemVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1] as const
    }
  }
};

export function PageHeroSection({
  title,
  description,
  subtitle,
  heroIcon,
  breadcrumbs,
  metadata,
  level,
  ctaButtons,
  hubStats,
  showContentInfo = true,
  children
}: PageHeroSectionProps) {
  return (
    <section className={`${styles.pageHeader} ${styles[`level${level}`]}`}>
      {/* Unified background elements */}
      <div className={styles.backgroundPattern} aria-hidden="true" />
      <div className={styles.backgroundAnimation} aria-hidden="true" />
      
      <motion.div
        className={styles.container}
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {/* Breadcrumbs - Always shown, unified position */}
        <motion.div
          className={styles.breadcrumbWrapper}
          variants={itemVariants}
        >
          <Breadcrumb items={breadcrumbs} className={styles.breadcrumb} />
        </motion.div>
        
        {/* Title section - Unified structure */}
        <motion.div className={styles.titleSection} variants={itemVariants}>
          {/* Hero Icon for Level 1 - Encourages action */}
          {heroIcon && level === 1 && (
            <span className={styles.heroIcon}>
              {heroIcon}
            </span>
          )}
          
          {/* Title - Same for all levels */}
          <h1 className={styles.title}>
            <span className={styles.titleMain}>{title}</span>
          </h1>
          
          {/* Description - Always shown */}
          <p 
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          
          {/* Subtitle if provided */}
          {subtitle && (
            <p 
              className={styles.subtitle}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          )}
        </motion.div>
        
        {/* Level-specific functional content */}
        <motion.div 
          className={styles.levelSpecificContent} 
          variants={itemVariants}
        >
          {/* Level 1: Call to Action Buttons */}
          {level === 1 && ctaButtons && ctaButtons.length > 0 && (
            <div className={styles.ctaSection}>
              <div className={styles.ctaButtons}>
                {ctaButtons.map((button, index) => (
                  <TrailingLink
                    key={index}
                    href={button.href}
                    className={`${styles.ctaButton} ${button.variant === 'primary' ? styles.primary : ''}`}
                  >
                    {button.text}
                  </TrailingLink>
                ))}
              </div>
            </div>
          )}
          
          {/* Level 2: Hub Statistics */}
          {level === 2 && hubStats && hubStats.length > 0 && (
            <div className={styles.hubStats}>
              {hubStats.map((stat, index) => (
                <motion.div 
                  key={index} 
                  className={styles.statItem}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {stat.icon && (
                    <span className={styles.statIcon}>{stat.icon}</span>
                  )}
                  <span className={styles.statNumber}>{stat.number}</span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </motion.div>
              ))}
            </div>
          )}
          
          {/* Level 3: Content Information */}
          {level === 3 && showContentInfo && metadata && (
            <div className={styles.contentInfo}>
              {/* Metadata */}
              <div className={styles.metadata}>
                {metadata.category && (
                  <span className={styles.category}>{metadata.category}</span>
                )}
                {metadata.publishedAt && (
                  <time className={styles.date} dateTime={metadata.publishedAt}>
                    {new Date(metadata.publishedAt).toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </time>
                )}
                {metadata.readingTime && (
                  <span className={styles.readingTime}>{metadata.readingTime}</span>
                )}
                {metadata.author && (
                  <span className={styles.author}>{metadata.author}</span>
                )}
              </div>
              
              {/* Tags */}
              {metadata.tags && metadata.tags.length > 0 && (
                <div className={styles.tags}>
                  {metadata.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
        
        {/* Additional custom content if needed */}
        {children}
      </motion.div>
    </section>
  );
}

export default PageHeroSection;